import { useContext } from 'react'

import { match } from 'ts-pattern'
import { FetchAppointmentNotification } from 'types/graphql'

import { Redirect, routes, useMatch, useRoutePaths } from '@cedarjs/router'

import { usePatientPortalAuth } from 'src/auth'
import LoadingSpinner from 'src/components/atoms/LoadingSpinner/LoadingSpinner'
import { useAppointmentConfirmation } from 'src/hooks/useAppointmentConfirmation/useAppointmentConfirmation'
import NotFoundPage from 'src/pages/NotFoundPage/NotFoundPage'
import { useKioskMode } from 'src/providers/context/KioskModeContext/KioskModeContext'
import { KioskSessionEnded } from 'src/providers/context/KioskModeContext/KioskSessionEnded'
import { createNamedContext } from 'src/utils'

import { checkInAuthGate } from './checkInAuthGate'
import { useCheckInNavigation } from './useCheckInNavigation'
import { useSelfCheckInAppointment } from './useSelfCheckInAppointment'

type AppointmentConfirmation = NonNullable<
  FetchAppointmentNotification['appointmentConfirmation']
>

const PatientSelfCheckInContext = createNamedContext<{
  appointmentConfirmation: AppointmentConfirmation
}>('PatientSelfCheckInContext')

export const usePatientSelfCheckInContext = () => {
  const context = useContext(PatientSelfCheckInContext)

  if (!context) {
    throw new Error(
      'usePatientSelfCheckInContext must be used within a PatientSelfCheckInContextProvider'
    )
  }

  return context
}

export const PatientSelfCheckInContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    appointmentId,
    notificationId,
    isOnAppointmentIdRoute,
    checkInRoute,
  } = useCheckInNavigation()

  const { isAuthenticated, loading: authLoading } = usePatientPortalAuth()
  const { isKioskMode, kioskTaskHubRoute } = useKioskMode()
  const authGate = checkInAuthGate({
    isOnAppointmentIdRoute,
    isAuthenticated,
    authLoading,
    notificationId,
  })

  const byNotification = useAppointmentConfirmation({
    notificationId,
    skip: isOnAppointmentIdRoute,
  })

  const byAppointment = useSelfCheckInAppointment({
    appointmentId,
    appointmentNotificationId: notificationId,
    skip: !isOnAppointmentIdRoute || authGate !== 'proceed',
  })

  const routePaths = useRoutePaths()
  const isSuccessPage = useMatch(routePaths.patientSelfCheckInSuccess)

  const gateElement = match(authGate)
    .with('wait', () => <LoadingSpinner />)
    .with('login', () => <>{children}</>)
    .with('notFound', () =>
      isKioskMode ? <KioskSessionEnded /> : <NotFoundPage />
    )
    .with('proceed', () => null)
    .exhaustive()

  if (gateElement) return gateElement

  if (byNotification.loading || byAppointment.loading) {
    return <LoadingSpinner />
  }

  const appointmentConfirmation =
    byAppointment.data?.selfCheckInAppointment ??
    byNotification.data?.appointmentConfirmation

  if (!appointmentConfirmation) {
    return isKioskMode ? <KioskSessionEnded /> : <NotFoundPage />
  }

  const { patientCheckInCompletedAt } = appointmentConfirmation

  if (!appointmentConfirmation.canCheckIn) {
    return (
      <Redirect
        to={
          kioskTaskHubRoute ??
          routes.patientDashboardChooser({
            notificationId: appointmentConfirmation.id,
          })
        }
        options={{ replace: true }}
      />
    )
  }

  if (patientCheckInCompletedAt && !isSuccessPage.match) {
    return (
      <Redirect
        to={checkInRoute('patientSelfCheckInSuccess', {
          appointmentId: appointmentConfirmation.appointmentId,
        })}
        options={{ replace: true }}
      />
    )
  }

  if (!patientCheckInCompletedAt && isSuccessPage.match) {
    return (
      <Redirect
        to={
          kioskTaskHubRoute ??
          routes.patientSelfCheckInIntro({
            notificationId: appointmentConfirmation.id,
          })
        }
        options={{ replace: true }}
      />
    )
  }

  return (
    <PatientSelfCheckInContext.Provider
      value={{
        appointmentConfirmation,
      }}
    >
      {children}
    </PatientSelfCheckInContext.Provider>
  )
}
